/* ──────────────────────────────────────────────────────────────────────────
 * Field Officer Copilot — §13.
 *
 * The brief gives four example questions and then one hard constraint:
 *
 *   "But the Copilot should not autonomously command critical government
 *    action. It is an intelligence interface into the operational system."
 *
 * That sentence decides the architecture. This is a READ interface. It has
 * no write path to anything — not to the incident machine, not to a gate,
 * not to a PA system. A request to act is recognised, refused, and turned
 * into the authorised route the officer should actually use.
 *
 * ── HOW IT ANSWERS ──
 * Intent is classified deterministically and the classification is SHOWN.
 * The copilot then routes through §18 to the engine that owns the answer
 * and filters the result through §17 for the asking officer's role. It
 * does not generate prose about operational state; it reports what the
 * engines returned.
 *
 * Three refusals are built in rather than bolted on:
 *
 *   1. COMMANDS. "Close gate 3" is refused and redirected to the
 *      authorisation workflow.
 *   2. UNRECOGNISED QUESTIONS. It says it did not understand and lists
 *      what it can answer. A copilot that guesses at an operational
 *      question is worse than one that admits the limit — the officer
 *      cannot tell a guess from an answer.
 *   3. OUT-OF-CLEARANCE requests. Refused by §17 with the reason, not
 *      silently emptied.
 * ────────────────────────────────────────────────────────────────────────── */

import { execute } from "./router";

import { canAccess, redactFor } from "../governance/privacy";
import { correlateHazards } from "../geo/services/multiHazard";
import { assessServiceStress } from "../geo/services/serviceStress";
import { haversineKm } from "../geo/services/emergencyResponse";
import { buildTwin } from "../twin/twin";

/* ── Intents ───────────────────────────────────────────────────────────
 * Each declares the phrases that select it, the data category it reads,
 * and the purpose it reads under. Purpose is per-intent, so a widened
 * intent cannot widen what the copilot may look at. */
export const INTENTS = {
  top_risks: {
    id: "top_risks",
    label: { en: "Top risks in a sector", mr: "सेक्टरमधील प्रमुख धोके" },
    example: "What are my top risks in Sector 1?",
    patterns: ["risk", "risks", "danger", "hazard", "worst", "concern", "problem"],
    category: "sector_state",
    purpose: "CROWD_SAFETY",
  },
  sop_lookup: {
    id: "sop_lookup",
    label: { en: "Procedure lookup", mr: "कार्यपद्धती" },
    example: "What is the approved SOP for crowd diversion?",
    patterns: ["sop", "procedure", "protocol", "guideline", "how do i", "what should i do", "rule"],
    category: null,           // documents, not operational data
    purpose: null,
  },
  medical_capacity: {
    id: "medical_capacity",
    label: { en: "Medical capacity", mr: "वैद्यकीय क्षमता" },
    example: "Which medical camp has capacity?",
    patterns: ["medical", "camp", "hospital", "bed", "beds", "capacity", "casualty", "treat"],
    category: "medical_capacity",
    purpose: "EMERGENCY_RESPONSE",
  },
  nearby_incidents: {
    id: "nearby_incidents",
    label: { en: "Nearby incidents", mr: "जवळच्या घटना" },
    example: "Show unresolved critical incidents within 1 km",
    patterns: ["incident", "incidents", "unresolved", "open", "nearby", "within", "critical", "around me"],
    category: "incident",
    purpose: "CROWD_SAFETY",
  },
  service_status: {
    id: "service_status",
    label: { en: "Service status", mr: "सेवा स्थिती" },
    example: "Is sanitation holding up in Sector 2?",
    patterns: ["toilet", "sanitation", "water", "food", "shelter", "parking", "service", "queue"],
    category: "sector_state",
    purpose: "CROWD_SAFETY",
  },
  crowd_forecast: {
    id: "crowd_forecast",
    label: { en: "Crowd forecast", mr: "गर्दी अंदाज" },
    example: "How busy will Ramkund be in 30 minutes?",
    patterns: ["forecast", "predict", "will", "later", "next hour", "in 30", "expect", "busy"],
    category: "forecast",
    purpose: "CROWD_SAFETY",
  },
};

/* Verbs that mean "do something". Recognised so they can be refused —
 * a copilot that quietly answers a command as though it were a question
 * has already blurred the line the brief drew. */
const COMMAND_VERBS = [
  "close", "open", "shut", "divert", "deploy", "dispatch", "send",
  "hold", "stop", "halt", "announce", "broadcast", "authorise", "authorize",
  "approve", "execute", "activate", "trigger", "order", "instruct",
];

const SECTOR_PATTERN = /\bsec(?:tor)?[\s-]*0*(\d)\b/i;
const NAME_TO_SECTOR = {
  ramkund: "SEC-01", panchavati: "SEC-02", trimbak: "SEC-03",
  trimbakeshwar: "SEC-03", kushavarta: "SEC-03", tapovan: "SEC-04",
  "nashik road": "SEC-05", gangapur: "SEC-06",
};

function extractSector(q) {
  const m = q.match(SECTOR_PATTERN);
  if (m) return `SEC-0${m[1]}`;
  const hit = Object.keys(NAME_TO_SECTOR).find((n) => q.includes(n));
  return hit ? NAME_TO_SECTOR[hit] : null;
}

function extractRadiusKm(q) {
  const m = q.match(/(\d+(?:\.\d+)?)\s*(km|kilometre|kilometer|m\b|metre|meter)/i);
  if (!m) return null;
  const v = parseFloat(m[1]);
  return /^m/i.test(m[2]) ? v / 1000 : v;
}

/* ── Classification ────────────────────────────────────────────────────
 * Deterministic scoring. Returns the intent AND the score so the UI can
 * show the officer what the copilot thought it was being asked — which
 * lets them catch a misread before acting on the answer. */
export function classify(question) {
  const q = String(question ?? "").toLowerCase().trim();
  if (!q) return { intent: null, isCommand: false, reason: "Empty question." };

  const words = q.split(/[^a-z0-9]+/).filter(Boolean);
  const commandVerb = COMMAND_VERBS.find((v) => words[0] === v || words.includes(v));

  const scored = Object.values(INTENTS)
    .map((intent) => ({
      intent,
      score: intent.patterns.reduce((a, p) => a + (q.includes(p) ? 1 : 0), 0),
    }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score);

  return {
    intent: scored[0]?.intent ?? null,
    score: scored[0]?.score ?? 0,
    alternatives: scored.slice(1, 3).map((r) => r.intent.id),
    isCommand: Boolean(commandVerb),
    commandVerb: commandVerb ?? null,
    sector: extractSector(q),
    radiusKm: extractRadiusKm(q),
  };
}

/* ── Ask ───────────────────────────────────────────────────────────────── */
export function ask(question, {
  liveState, twin = null, role = "FIELD", incidents = [], officerCoord = null,
} = {}) {
  const t = twin ?? (liveState ? buildTwin(liveState) : null);
  const c = classify(question);

  const base = {
    question,
    intent: c.intent?.id ?? null,
    intentLabel: c.intent?.label ?? null,
    matchScore: c.score ?? 0,
    /* Always shown. The officer can see what was understood. */
    interpretedAs: c.intent
      ? `${c.intent.label.en}${c.sector ? ` — ${c.sector}` : ""}`
      : null,
  };

  /* 1. Commands are refused before anything else is considered. */
  if (c.isCommand) {
    return {
      ...base,
      answered: false,
      refused: true,
      refusalKind: "command",
      message:
        `This is a request to act ("${c.commandVerb}"), not a question. The copilot is a read-only intelligence interface and cannot command operational action.`,
      whatToDoInstead:
        "Open the intervention simulator to compare options, then take the chosen option through the incident workflow for an authorised officer to approve. The action is executed from there, against a recorded decision.",
    };
  }

  /* 2. No recognised intent — say so, rather than guessing. */
  if (!c.intent) {
    return {
      ...base,
      answered: false,
      refused: true,
      refusalKind: "not_understood",
      message:
        "The copilot did not recognise this question and will not guess. An officer cannot tell a guess from an answer.",
      canAnswer: Object.values(INTENTS).map((i) => ({
        id: i.id, label: i.label, example: i.example,
      })),
    };
  }

  /* 3. Clearance and purpose, where the intent reads operational data. */
  if (c.intent.category) {
    const gate = canAccess(role, c.intent.category, c.intent.purpose);
    if (!gate.allowed) {
      return {
        ...base,
        answered: false,
        refused: true,
        refusalKind: "access",
        message: gate.reason,
      };
    }
  }

  /* 4. Answer, from the engine that owns the answer. */
  const answer = (() => {
    switch (c.intent.id) {
      case "sop_lookup": {
        const r = execute("sop_lookup", { query: question });
        const res = r.result;
        return {
          kind: "documents",
          matched: res.matched,
          documents: res.matches,
          disclaimer: res.disclaimer,
          /* Repeated at answer level so it cannot be lost in rendering. */
          warning: res.matched
            ? "These are ILLUSTRATIVE procedures, not approved SOPs. Verify against the district administration's issued procedure before acting."
            : res.note,
        };
      }

      case "top_risks": {
        if (!c.sector) return needSector("which sector");
        const hz = correlateHazards(liveState, {
          twin: t, serviceStress: assessServiceStress(liveState),
        });
        const row = hz.sectors.find((s) => s.id === c.sector);
        if (!row) return { kind: "none", message: `No data for ${c.sector}.` };
        return {
          kind: "risks",
          sector: c.sector,
          level: row.level,
          summary: row.narrative,
          top: row.hazards
            .filter((h) => h.severity != null && h.severity > 0.2)
            .sort((a, b) => b.severity - a.severity)
            .slice(0, 4)
            .map((h) => ({ key: h.key, label: h.label, severity: h.severity, reading: h.reading })),
          compounding: row.interactions.slice(0, 2).map((i) => i.why),
          /* The gap travels with the answer. */
          notMonitored: row.unmonitored.map((u) => u.label.en),
        };
      }

      case "medical_capacity": {
        const facilities = [...t.byType("hospital"), ...t.byType("medicalCamp")]
          .map((f) => ({
            id: f.id, name: f.name, coord: f.coord, sector: f.sector,
            bedsFree: f.state?.bedsFree ?? null,
            emergencyFree: f.state?.emergencyFree ?? null,
            trauma: f.state?.trauma ?? null,
            distanceKm: officerCoord && f.coord
              ? Number((haversineKm(officerCoord, f.coord) ?? 0).toFixed(2))
              : null,
          }))
          /* Unknown capacity sorts last, never first. */
          .sort((a, b) => (b.emergencyFree ?? -1) - (a.emergencyFree ?? -1));
        return {
          kind: "capacity",
          facilities,
          withCapacity: facilities.filter((f) => f.emergencyFree > 0).length,
          unknown: facilities.filter((f) => f.emergencyFree == null).length,
          note: "Capacity figures are simulated. Confirm by radio before transporting a casualty.",
        };
      }

      case "nearby_incidents": {
        if (!officerCoord) {
          return {
            kind: "needs_input",
            message: "Your position is not available, so 'nearby' cannot be computed. Specify a sector instead.",
          };
        }
        const radius = c.radiusKm ?? 1;
        const near = incidents
          .filter((i) => !["resolved", "cancelled"].includes(i.status))
          .map((i) => ({ ...i, distanceKm: haversineKm(officerCoord, i.coord) }))
          .filter((i) => i.distanceKm != null && i.distanceKm <= radius)
          .sort((a, b) => a.distanceKm - b.distanceKm);
        return {
          kind: "incidents",
          radiusKm: radius,
          count: near.length,
          incidents: near.map((i) => ({
            id: i.id, sector: i.sector, status: i.status,
            severity: i.severity ?? null,
            distanceKm: Number(i.distanceKm.toFixed(2)),
          })),
        };
      }

      case "service_status": {
        const rows = assessServiceStress(liveState)
          .filter((r) => !c.sector || r.sectorId === c.sector);
        const notable = rows.filter((r) => r.state !== "ok").slice(0, 5);
        return {
          kind: "services",
          sector: c.sector,
          atRisk: notable.map((r) => ({
            service: r.service, label: r.label, state: r.state,
            utilisationPct: r.utilisationPct,
            minutesToStress: r.minutesToStress,
            narrative: r.narrative,
          })),
          allClear: notable.length === 0,
        };
      }

      case "crowd_forecast": {
        const r = execute("crowd_forecast", { liveState });
        const pick = (h) => r.result[h]
          .filter((s) => !c.sector || s.id === c.sector)
          .map((s) => ({ id: s.id, name: s.name, band: s.band, occupancyPct: s.occupancyPct }));
        return {
          kind: "forecast",
          sector: c.sector,
          horizons: { 15: pick(15), 30: pick(30), 60: pick(60) },
          note: "Projection from a simulated model. It measures internal consistency, not real-world accuracy.",
        };
      }

      default:
        return { kind: "none", message: "No handler for this intent." };
    }
  })();

  /* 5. Filter the answer for the asking role before it is returned. */
  const { data, removed } = redactFor(role, answer);

  return {
    ...base,
    answered: true,
    refused: false,
    role,
    answer: data,
    redactedFields: removed,
    /* Stated on every answer, not only on the refusals. */
    advisoryOnly:
      "Decision support only. The copilot cannot authorise or execute any action.",
  };
}

const needSector = (what) => ({
  kind: "needs_input",
  message: `Please say ${what}. The copilot will not pick one for you — answering about the wrong sector is worse than asking.`,
});

export const COPILOT_ASSUMPTIONS = [
  "The copilot is read-only. It has no write path to the incident machine, a gate, or a PA system, so it cannot command action even if asked directly.",
  "Intent is classified deterministically and always shown, so an officer can catch a misread before acting on the answer.",
  "An unrecognised question is refused with a list of what can be answered. A copilot that guesses at an operational question is worse than one that admits the limit.",
  "Every answer is filtered through the §17 redaction pass for the asking officer's role, so clearance is enforced at the point of answering rather than in the UI.",
  "SOP answers carry the illustrative-only warning at both document and answer level, because a rendering that drops it would leave invented procedure looking authoritative.",
  "Where the copilot needs a sector and was not given one, it asks. Answering about the wrong sector is worse than asking.",
];
